// lib/errors.ts — describeError() centralizes error -> human copy mapping for
// every toast/inline error in the app. Never surface a raw exception message;
// always go through this so copy stays consistent and editable in one place.
import { ApiError, apiBase } from "@/lib/api-client";

export type DescribedError = {
  title: string;
  description?: string;
  hint?: string;
};

// ponytail: pt-BR only — the rest of the app is monolingual and every
// caller defaulted to English by accident. Two locales here made
// the "English copy is shipped because nobody passes pt-BR" failure
// mode silently. Collapse to a single locale.
const COPY = {
  fallbackAction: "concluir esta ação",
  network: {
    title: "Não foi possível contatar a API",
    description: (action: string) => `Não foi possível alcançar ${apiBase} ao tentar ${action}.`,
    hint: "Na maioria das vezes o backend não está em execução. Inicie com `bun run dev` na raiz do repositório (Postgres + backend), ou `docker compose up -d postgres` + `cd apps/backend && bun run dev`.",
  },
  unauthorized: {
    title: "Não autorizado",
    description: (action: string) =>
      `A API rejeitou a chave ao tentar ${action}. Verifique se VITE_API_KEY corresponde ao backend.`,
  },
  forbidden: {
    title: "Permissão negada",
    description: (action: string) => `Você não tem acesso para ${action}.`,
  },
  server: {
    title: "Erro do servidor",
    description: (action: string) =>
      `O servidor encontrou um erro ao tentar ${action}. Tente novamente em instantes.`,
  },
  // ponytail: drop the redundant "Não foi possível" prefix here —
  // `queryCache.onError` already wraps the toast title with that
  // prefix when the caller passes `describeError(error).title`
  // as-is (see main.tsx). Keep the noun phrase readable on its own.
  generic: (action: string) =>
    action === COPY.fallbackAction ? "Falha na operação" : `Falha na operação: ${action}`,
  unknown: "Erro desconhecido.",
} as const;

export function describeError(err: unknown, opts: { action?: string } = {}): DescribedError {
  const a = opts.action ?? COPY.fallbackAction;
  if (err instanceof Error) {
    // ponytail: prefer the status code from ApiError when available —
    // the upstream message is free-form and rarely matches the
    // generic "network/401/500" strings below. Falling back to the
    // message keeps the old behaviour for non-ApiError throws.
    const status = err instanceof ApiError ? err.status : undefined;
    const msg = err.message.toLowerCase();
    if (status === 0 || (!status && (msg.includes("network") || msg.includes("failed to fetch")))) {
      // The most common cause of "Failed to fetch" in dev is that the backend
      // isn't running — the SPA boots fine on its own but every API call fails.
      // Surface that explicitly so the user doesn't waste time checking wifi.
      return {
        title: COPY.network.title,
        description: COPY.network.description(a),
        hint: COPY.network.hint,
      };
    }
    if (status === 401 || (!status && (msg.includes("401") || msg.includes("unauthorized")))) {
      return { title: COPY.unauthorized.title, description: COPY.unauthorized.description(a) };
    }
    if (status === 403 || (!status && (msg.includes("403") || msg.includes("forbidden")))) {
      return { title: COPY.forbidden.title, description: COPY.forbidden.description(a) };
    }
    if (status === 500 || (!status && (msg.includes("500") || msg.includes("server")))) {
      return { title: COPY.server.title, description: COPY.server.description(a) };
    }
    return {
      title: COPY.generic(a),
      description: err.message,
    };
  }
  return {
    title: COPY.generic(a),
    description: typeof err === "string" ? err : COPY.unknown,
  };
}
